import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Heading,
  Divider,
  SimpleGrid,
  Textarea,
  useColorModeValue,
  List,
  ListItem,
  InputGroup,
  InputRightElement,
  Text,
  Select,
  useToast,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { getData, postData } from '../../utils/httpClient.js';
import PageTable from 'components/layout/PageTable';

export default function CreateCustomerPayment() {
  const [form, setForm] = useState({
    cliente_id: '',
    fecha_pago: '',
    metodo_pago: 1,
    referencia: '',
    observaciones: '',
    monto_total: 0,
  });

  const [clients, setClients] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    loadClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClients = async () => {
    try {
      const response = await getData('/invoices/customers');
      setClients(response?.payload?.customers || []);
    } catch (error) {
      toast({
        title: 'Error al cargar clientes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadPendingInvoices = async (customerId) => {
    try {
      const response = await getData(
        `/invoices/byCustomerId/${customerId}?status=Pendiente`,
      );
      setPendingInvoices(response?.payload?.invoices || []);
    } catch (error) {
      toast({
        title: 'Error al cargar facturas pendientes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleClientSearch = (value) => {
    setClientSearch(value);
    if (value.trim()) {
      const filtered = clients.filter(
        (client) =>
          client.name?.toLowerCase().includes(value.toLowerCase()) ||
          client.documentNumber?.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredClients(filtered);
      setShowClientSuggestions(true);
    } else {
      setShowClientSuggestions(false);
    }
  };

  const selectClient = (client) => {
    setForm((prev) => ({
      ...prev,
      cliente_id: client.id,
      cliente: client,
    }));
    setClientSearch(`${client.name} - ${client.documentNumber}`);
    setShowClientSuggestions(false);
    loadPendingInvoices(client.id);
    setSelectedInvoices([]);
  };

  const toggleInvoiceSelection = (invoice) => {
    setSelectedInvoices((prev) => {
      const exists = prev.find((inv) => inv.id === invoice.id);
      if (exists) {
        return prev.filter((inv) => inv.id !== invoice.id);
      }
      return [...prev, invoice];
    });
  };

  useEffect(() => {
    const total = selectedInvoices.reduce(
      (sum, invoice) => sum + (Number(invoice.totalAmount) || 0),
      0,
    );
    setForm((prev) => ({ ...prev, monto_total: total }));
  }, [selectedInvoices]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-UY');
  };

  const handleSubmit = async () => {
    if (!form.cliente_id || selectedInvoices.length === 0 || !form.fecha_pago) {
      toast({
        title: 'Campos requeridos',
        description:
          'Complete cliente, fecha de pago y seleccione al menos una factura',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        invoiceCustomerId: form.cliente_id,
        paymentDate: form.fecha_pago,
        paymentType: Number(form.metodo_pago),
        paymentReference: form.referencia,
        observations: form.observaciones,
        paymentAmount: form.monto_total,
        invoices: selectedInvoices.map((invoice) => ({ id: invoice.id })),
      };

      await postData('/customer-payments', payload);

      toast({
        title: 'Recibo registrado exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setForm({
        cliente_id: '',
        fecha_pago: '',
        metodo_pago: 1,
        referencia: '',
        observaciones: '',
        monto_total: 0,
      });
      setClientSearch('');
      setPendingInvoices([]);
      setSelectedInvoices([]);
    } catch (error) {
      toast({
        title: 'Error al registrar recibo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTable
      flexDirection="column"
      w="100%"
      px={{ base: 3, md: 4 }}
      py={{ base: 3, md: 4 }}
      stickyActions={false}
    >
      <VStack spacing={4} align="stretch">
        <Heading size="md" color="secondaryGray.900">
          Registrar Recibo
        </Heading>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Cliente</FormLabel>
            <InputGroup>
              <Input
                size="sm"
                value={clientSearch}
                onChange={(e) => handleClientSearch(e.target.value)}
                placeholder="Buscar por nombre o documento"
              />
              <InputRightElement>
                <SearchIcon />
              </InputRightElement>
            </InputGroup>

            {showClientSuggestions && filteredClients.length > 0 && (
              <Box
                position="relative"
                bg={bgColor}
                border="1px"
                borderColor={borderColor}
                borderRadius="md"
                mt={1}
                maxH="200px"
                overflowY="auto"
                zIndex={10}
              >
                <List>
                  {filteredClients.slice(0, 10).map((client) => (
                    <ListItem
                      key={client.id}
                      p={2}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => selectClient(client)}
                    >
                      <Text fontSize="sm" fontWeight="medium" color="blue.600">
                        {client.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Doc: {client.documentNumber}
                      </Text>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm">Fecha de Recibo</FormLabel>
            <Input
              size="sm"
              type="date"
              value={form.fecha_pago}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, fecha_pago: e.target.value }))
              }
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm">Método de Pago</FormLabel>
            <Select
              size="sm"
              value={form.metodo_pago}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  metodo_pago: parseInt(e.target.value, 10),
                }))
              }
            >
              <option value={3}>Transferencia Bancaria</option>
              <option value={2}>Cheque</option>
              <option value={1}>Efectivo</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Referencia / Comprobante</FormLabel>
            <Input
              size="sm"
              value={form.referencia}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, referencia: e.target.value }))
              }
              placeholder="Número de comprobante, transferencia, etc."
            />
          </FormControl>
        </SimpleGrid>

        <FormControl>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea
            size="sm"
            value={form.observaciones}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, observaciones: e.target.value }))
            }
            placeholder="Observaciones del recibo"
          />
        </FormControl>

        <Divider />

        {form.cliente_id && (
          <VStack spacing={4} align="stretch">
            <Heading size="sm">Facturas pendientes</Heading>

            {pendingInvoices.length > 0 ? (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Seleccionar</Th>
                      <Th>Número</Th>
                      <Th>Fecha</Th>
                      <Th>Vencimiento</Th>
                      <Th>Total</Th>
                      <Th>Estado</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pendingInvoices.map((invoice) => (
                      <Tr key={invoice.id}>
                        <Td>
                          <Checkbox
                            isChecked={selectedInvoices.some(
                              (inv) => inv.id === invoice.id,
                            )}
                            onChange={() => toggleInvoiceSelection(invoice)}
                          />
                        </Td>
                        <Td>
                          {invoice.invoiceId ||
                            `${invoice.series}-${invoice.number}`}
                        </Td>
                        <Td>{formatDate(invoice.issueDate)}</Td>
                        <Td>{formatDate(invoice.dueDate)}</Td>
                        <Td>
                          ${Number(invoice.totalAmount || 0).toFixed(2)}
                        </Td>
                        <Td>
                          <Badge colorScheme="yellow">
                            {invoice.accountStatus || 'Pendiente'}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Text color="gray.500" textAlign="center">
                No hay facturas pendientes para este cliente
              </Text>
            )}
          </VStack>
        )}

        {selectedInvoices.length > 0 && (
          <>
            <Divider />
            <HStack justify="space-between" align="center">
              <Text fontSize="sm" color="secondaryGray.600">
                Facturas seleccionadas:{' '}
                <Text as="span" fontWeight="medium">
                  {selectedInvoices.length}
                </Text>
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="green.600">
                Total a cobrar: ${form.monto_total?.toFixed(2) || '0.00'}
              </Text>
            </HStack>
          </>
        )}

        <HStack justify="end">
          <Button
            size="sm"
            colorScheme="gray"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Registrando..."
            isDisabled={selectedInvoices.length === 0 || !form.cliente_id}
          >
            Registrar Recibo
          </Button>
        </HStack>
      </VStack>
    </PageTable>
  );
}
